export const isAdmin = (
  authUser: { email?: string | null } | null | undefined,
) => {
  return (
    !!authUser &&
    (authUser.email === "niklas.lavrell@gmail.com" ||
      authUser.email === "soderquist.hanna@gmail.com")
  );
};

type Timestamp = { toDate: () => Date; toMillis: () => number };

type ActivityUser = {
  createdAt?: Timestamp;
  lastSignInAt?: Timestamp;
  lastUpdatedAt?: Timestamp;
  rank?: { general: number };
};

type SortableUser = ActivityUser & {
  username?: string;
  firstName?: string;
  lastName?: string;
  published?: boolean;
  images?: readonly unknown[];
  categories?: readonly unknown[];
  subscription?: { plan: string };
  messageMetadata?: { messagesCount: number; answeredMessagesCount: number };
};

export type SortKey =
  | "username"
  | "firstName"
  | "lastName"
  | "published"
  | "images"
  | "categories"
  | "plan"
  | "messagesCount"
  | "answeredMessagesCount"
  | "createdAt"
  | "lastSignInAt"
  | "lastUpdatedAt"
  | "rank";

export type SortDirection = "ascending" | "descending";

export type Sort = { key: SortKey; direction: SortDirection };

const DAY_IN_MILLIS = 24 * 60 * 60 * 1000;
const INACTIVE_AFTER_DAYS = 180;

const assertNever = (value: never): never => {
  throw new Error(`Unexpected value: ${value}`);
};

const nonEmpty = (text?: string) => (text ? text : undefined);

/** Returns undefined for missing values, which always sort last. */
const getSortValue = (
  user: SortableUser,
  sortKey: SortKey,
): string | number | undefined => {
  switch (sortKey) {
    case "username":
    case "firstName":
    case "lastName":
      return nonEmpty(user[sortKey]);
    case "plan":
      return nonEmpty(user.subscription?.plan);
    case "published":
      return user.published ? 1 : 0;
    case "images":
    case "categories":
      return user[sortKey]?.length ?? 0;
    case "messagesCount":
    case "answeredMessagesCount":
      return user.messageMetadata?.[sortKey] ?? 0;
    case "createdAt":
    case "lastSignInAt":
    case "lastUpdatedAt":
      return user[sortKey]?.toMillis();
    case "rank":
      return user.rank?.general;
    default:
      return assertNever(sortKey);
  }
};

const getDefaultDirection = (sortKey: SortKey): SortDirection => {
  switch (sortKey) {
    case "username":
    case "firstName":
    case "lastName":
    case "plan":
      return "ascending";
    case "published":
    case "images":
    case "categories":
    case "messagesCount":
    case "answeredMessagesCount":
    case "createdAt":
    case "lastSignInAt":
    case "lastUpdatedAt":
    case "rank":
      return "descending";
    default:
      return assertNever(sortKey);
  }
};

/** Clicking the current column flips its direction; a new column starts in its default direction. */
export const getNextSort = (current: Sort, sortKey: SortKey): Sort => {
  if (current.key !== sortKey) {
    return { key: sortKey, direction: getDefaultDirection(sortKey) };
  }
  const direction =
    current.direction === "ascending" ? "descending" : "ascending";
  return { key: sortKey, direction };
};

export const DEFAULT_SORT = {
  key: "rank",
  direction: "descending",
} as const satisfies Sort;

const compareValues = (a: string | number, b: string | number) =>
  typeof a === "string" && typeof b === "string"
    ? a.localeCompare(b, "sv")
    : Number(a) - Number(b);

export const sortUsers = <T extends SortableUser>(
  users: readonly T[],
  sort: Sort,
) => {
  const sign = sort.direction === "ascending" ? 1 : -1;
  return [...users].sort((userA, userB) => {
    const a = getSortValue(userA, sort.key);
    const b = getSortValue(userB, sort.key);
    if (a === undefined || b === undefined) {
      return Number(a === undefined) - Number(b === undefined);
    }
    return sign * compareValues(a, b);
  });
};

export const formatDate = (timestamp?: Timestamp) =>
  timestamp ? timestamp.toDate().toLocaleDateString("sv-SE") : "–";

const daysSince = (timestamp: Timestamp) =>
  (Date.now() - timestamp.toMillis()) / DAY_IN_MILLIS;

export const isInactive = (user: ActivityUser) =>
  !user.lastSignInAt || daysSince(user.lastSignInAt) > INACTIVE_AFTER_DAYS;

export const countSignedInWithinDays = (
  users: readonly ActivityUser[],
  days: number,
) =>
  users.filter(
    (user) => user.lastSignInAt && daysSince(user.lastSignInAt) <= days,
  ).length;

export const countInactive = (users: readonly ActivityUser[]) =>
  users.filter(isInactive).length;

const toMonthKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

export type SignupMonth = { month: string; count: number };

/** Sign-ups per month, from the first sign-up until the current month, including empty months. */
export const getSignupsPerMonth = (
  users: readonly ActivityUser[],
): SignupMonth[] => {
  const signupDates = users.flatMap((user) =>
    user.createdAt ? [user.createdAt.toDate()] : [],
  );
  if (signupDates.length === 0) return [];

  const counts = new Map<string, number>();
  for (const date of signupDates) {
    const month = toMonthKey(date);
    counts.set(month, (counts.get(month) ?? 0) + 1);
  }

  const firstSignup = new Date(
    Math.min(...signupDates.map((date) => date.getTime())),
  );
  const now = new Date();
  const months: SignupMonth[] = [];
  for (
    const cursor = new Date(
      firstSignup.getFullYear(),
      firstSignup.getMonth(),
      1,
    );
    cursor <= now;
    cursor.setMonth(cursor.getMonth() + 1)
  ) {
    const month = toMonthKey(cursor);
    months.push({ month, count: counts.get(month) ?? 0 });
  }
  return months;
};
